/**
 * Berechnungs-Utilities für CherryPulse
 *
 * @module utils/calculations
 */

import type { Rating, TrendDirection, RatingScores } from '@/types';
import { calculateCherryScore as calculateCherryScoreFromStars } from './stars';

/**
 * Berechnet den CherryScore aus den Bewertungen.
 *
 * @param scores - Die Bewertungswerte im 3-Ebenen-Format
 * @returns Der CherryScore (0-5, höher = besser)
 */
export function calculateCherryScore(scores: RatingScores): number {
  return calculateCherryScoreFromStars(scores) ?? 0;
}

/**
 * Berechnet den CherryScore-Trend basierend auf den letzten beiden Bewertungen.
 *
 * @param ratings - Array von Bewertungen (mindestens 2 für einen Trend)
 * @returns Der Trend: 'up' (Verbesserung), 'down' (Verschlechterung), oder 'stable'
 */
export function calculateTrend(ratings: Rating[]): TrendDirection {
  if (ratings.length < 2) return 'stable';

  // Sortiere nach Datum (neueste zuerst)
  const sorted = [...ratings].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const recent = calculateCherryScore(sorted[0].scores);
  const previous = calculateCherryScore(sorted[1].scores);

  const diff = recent - previous;

  // Sterne-Logik: höherer Wert = besser
  // positive Differenz = Verbesserung = 'up'
  // negative Differenz = Verschlechterung = 'down'
  if (diff > 0.3) return 'up';
  if (diff < -0.3) return 'down';
  return 'stable';
}


/**
 * Formatiert ein ISO-8601-Datum im deutschen Kurzformat (nur Monat/Jahr).
 *
 * @param dateStr - ISO-8601-Datums-String (z.B. "2024-03-15T12:00:00Z")
 * @returns Formatiertes Datum (z.B. "März 2024")
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'short',
  });
}

/**
 * Formatiert ein ISO-8601-Datum mit vollem Datum und Uhrzeit.
 *
 * @param dateStr - ISO-8601-Datums-String (z.B. "2024-03-15T12:00:00Z")
 * @returns Formatiertes Datum mit Uhrzeit (z.B. "15. März 2024, 12:00 Uhr")
 */
export function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  const dateFormatted = date.toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timeFormatted = date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dateFormatted}, ${timeFormatted} Uhr`;
}

/**
 * Formatiert ein ISO-8601-Datum im kompakten deutschen Format.
 *
 * @param dateStr - ISO-8601-Datums-String (z.B. "2024-03-15T12:00:00Z")
 * @returns Formatiertes Datum (z.B. "15.03.2024, 12:00")
 */
export function formatDateTimeShort(dateStr: string): string {
  const date = new Date(dateStr);
  const dateFormatted = date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timeFormatted = date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dateFormatted}, ${timeFormatted}`;
}

/**
 * Formatiert eine Periode (YYYY-MM) in ein lesbares deutsches Format.
 *
 * @param period - Periode im Format "YYYY-MM"
 * @returns Formatierte Periode (z.B. "Mär 2024")
 */
export function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  const months = [
    'Jan',
    'Feb',
    'Mär',
    'Apr',
    'Mai',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Okt',
    'Nov',
    'Dez',
  ];
  return `${months[parseInt(month) - 1]} ${year}`;
}

/**
 * Kategorisiert einen Sterne-Score als gut, mittel oder schlecht.
 *
 * - ≥4: good (grün)
 * - 3-3.9: medium (amber)
 * - <3: poor (rot)
 *
 * @param score - Sterne-Score (0-5)
 * @returns Level-Kategorie
 */
export function getScoreLevel(
  score: number
): 'good' | 'medium' | 'poor' {
  if (score >= 4) return 'good';
  if (score >= 3) return 'medium';
  return 'poor';
}

/**
 * Gibt eine Tailwind-Text-Farbklasse fuer einen Sterne-Score zurueck.
 *
 * - ≥4: text-green-600
 * - ≥3: text-amber-600
 * - <3: text-red-600
 *
 * @param score - Sterne-Score (0-5)
 * @returns Tailwind text color class
 */
export function getScoreColorClass(score: number): string {
  if (score >= 4) return 'text-green-600';
  if (score >= 3) return 'text-amber-600';
  return 'text-red-600';
}

/**
 * Zeitraum-Typ für Filterung
 */
export type TimePeriod = 'month' | 'quarter' | 'year' | 'all';

/**
 * Filtert Bewertungen nach Zeitraum.
 *
 * @param ratings - Array von Bewertungen
 * @param period - Zeitraum ('month', 'quarter', 'year', 'all')
 * @returns Gefilterte Bewertungen innerhalb des Zeitraums
 */
export function filterRatingsByPeriod(ratings: Rating[], period: TimePeriod): Rating[] {
  if (period === 'all') return ratings;

  const now = new Date();
  let cutoffDate: Date;

  switch (period) {
    case 'month':
      cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      break;
    case 'quarter':
      cutoffDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      break;
    case 'year':
      cutoffDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      break;
    default:
      return ratings;
  }

  return ratings.filter(r => new Date(r.createdAt) >= cutoffDate);
}

/**
 * Gibt das deutsche Label für einen Zeitraum zurück.
 *
 * @param period - Zeitraum-Typ
 * @returns Deutsches Label
 */
export function getTimePeriodLabel(period: TimePeriod): string {
  switch (period) {
    case 'month':
      return 'Letzter Monat';
    case 'quarter':
      return 'Letztes Quartal';
    case 'year':
      return 'Letztes Jahr';
    case 'all':
      return 'Gesamt';
  }
}

/**
 * Dashboard-Zeitraumfilter.
 *
 * - `latest`: Nur die neueste Bewertung pro Agentur (keine Datumsgrenze)
 * - `thisYear`: Bewertungen ab 1. Jan des laufenden Jahres
 * - `lastYear`: Bewertungen des Vorjahres (1. Jan - 31. Dez)
 * - `all`: Keine Einschränkung
 */
export type DashboardPeriod = 'latest' | 'thisYear' | 'lastYear' | 'all';

/**
 * Label-Map für Dashboard-Zeiträume.
 */
export const DASHBOARD_PERIOD_LABELS: Record<DashboardPeriod, string> = {
  latest: 'Letzte Bewertung',
  thisYear: 'Dieses Jahr',
  lastYear: 'Letztes Jahr',
  all: 'Alle',
};

/**
 * Filtert Bewertungen nach Dashboard-Zeitraum.
 *
 * Für `latest` wird pro Agentur (`agencyId`) nur die neueste Bewertung
 * behalten. Für `thisYear` / `lastYear` wird nach `createdAt` gefiltert.
 * `all` gibt das unveränderte Array zurück.
 *
 * @param ratings - Array von Bewertungen
 * @param period - Dashboard-Zeitraum
 * @param now - Referenzzeitpunkt (optional, default: aktuelle Zeit)
 * @returns Gefilterte Bewertungen
 */
export function filterRatingsByDashboardPeriod(
  ratings: Rating[],
  period: DashboardPeriod,
  now: Date = new Date()
): Rating[] {
  if (period === 'all') return ratings;

  if (period === 'latest') {
    // Nur die neueste Bewertung pro Agentur behalten.
    const latestByAgency = new Map<string, Rating>();
    for (const r of ratings) {
      const existing = latestByAgency.get(r.agencyId);
      if (
        !existing ||
        new Date(r.createdAt).getTime() > new Date(existing.createdAt).getTime()
      ) {
        latestByAgency.set(r.agencyId, r);
      }
    }
    return Array.from(latestByAgency.values());
  }

  if (period === 'thisYear') {
    const start = new Date(now.getFullYear(), 0, 1);
    return ratings.filter((r) => new Date(r.createdAt) >= start);
  }

  // lastYear
  const start = new Date(now.getFullYear() - 1, 0, 1);
  const end = new Date(now.getFullYear(), 0, 1);
  return ratings.filter((r) => {
    const d = new Date(r.createdAt);
    return d >= start && d < end;
  });
}
